import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { onlineshopApiBaseUrl } from '../Filters';
import FilterOption from '../../components/FilterOption/FilterOption';
import FilterWrapper from '../../components/FilterWrapper/FilterWrapper';
import { IBrandFilterData } from '../../../../../Interface/ProductsFilterInterface';
import { useProductsFilter } from '../../../../../Context/ProductsFilterContext';
import useViewports from '../../../../../Hooks/useViewports';
import styles from './BrandFilter.module.scss?module';

const filterTarget = [
  'category_ids',
  'sale_percent',
  'sale_type',
  'has_stock',
  'sub_category_ids',
  'product_ids',
  'product_size_ids',
  'product_attribute_ids',
  'q',
  'favs_only',
];

const BrandFilter = () => {
  const { xs, sm, md } = useViewports();
  const isMobile = xs || sm || md;
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { filters, registerActiveFilterLabels } = useProductsFilter();
  const selectedBrandIds = filters.brand_ids;
  const [brandSearch, setBrandSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const onlyRealFiltersExceptItself = new URLSearchParams();

  filterTarget.map((filterKey) => {
    const val = searchParams.get(filterKey);
    if (val) onlyRealFiltersExceptItself.set(filterKey, val);
  });

  onlyRealFiltersExceptItself.set(
    'context[]',
    filters.view === 'grid'
      ? 'brand_product_sizes_count'
      : 'brand_attributes_count'
  );

  const brandFilter = useQuery<IBrandFilterData[]>({
    queryKey: [
      'brand_ids',
      onlyRealFiltersExceptItself.toString(), //refetch trigger only when view changes (grid & list) no need for table view refetch because list and table have the same data
      filters.view === 'grid' ? 'grid' : 'list',
    ],
    queryFn: async () => {
      const res = await axios.get(
        `${onlineshopApiBaseUrl}/brands?${onlyRealFiltersExceptItself.toString()}&sortBy=name`
      );
      return res.data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const { isFetching, isSuccess, isPending, data } = brandFilter;

  const selectedBrandsQuery = useQuery<IBrandFilterData[]>({
    queryKey: ['brand_ids', 'selected', selectedBrandIds.join(',')],
    queryFn: async () => {
      const res = await axios.get(
        `${onlineshopApiBaseUrl}/brands?brand_ids=${selectedBrandIds.join(',')}`
      );
      return res.data;
    },
    enabled: selectedBrandIds.length > 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const selectedBrandData = selectedBrandsQuery.data ?? [];

  const mergedOptions = useMemo<IBrandFilterData[]>(() => {
    const options = new Map<number, IBrandFilterData>();

    (data ?? []).forEach((option) => {
      options.set(option.erp_id, option);
    });

    // Ensure already selected brands remain visible even if filtered out by category.
    filters.brand_ids.forEach((idString) => {
      const idNumber = Number(idString);
      if (options.has(idNumber)) {
        return;
      }

      // Try to find the brand in any cached brand queries.
      const cachedMatch = queryClient
        .getQueriesData<IBrandFilterData[]>({ queryKey: ['brand_ids'] })
        .map(([, cachedData]) =>
          cachedData?.find((cachedBrand) => cachedBrand.erp_id === idNumber)
        )
        .find(Boolean);

      if (cachedMatch) {
        options.set(idNumber, cachedMatch);
        return;
      }

      const selectedMatch = selectedBrandData.find(
        (selectedBrand) => selectedBrand.erp_id === idNumber
      );
      if (selectedMatch) {
        options.set(idNumber, {
          ...selectedMatch,
          product_sizes_count: selectedMatch.product_sizes_count ?? 0,
        });
        return;
      }

      // Fallback stub to keep the checkbox rendered.
      options.set(idNumber, {
        erp_id: idNumber,
        id: idNumber,
        name: idString,
        product_sizes_count: 0,
      });
    });

    return Array.from(options.values());
  }, [data, filters.brand_ids, queryClient, selectedBrandData]);

  useEffect(() => {
    if (mergedOptions.length === 0 || selectedBrandIds.length === 0) return;

    const labelsToRegister = selectedBrandIds
      .map((id) => {
        const option = mergedOptions.find(
          (opt) => opt.erp_id.toString() === id.toString()
        );
        if (option) {
          return { value: id, label: option.name };
        }
        return null;
      })
      .filter(
        (item): item is { value: string; label: string } => item !== null
      );

    if (labelsToRegister.length > 0) {
      registerActiveFilterLabels('brand_ids', labelsToRegister);
    }
  }, [mergedOptions, selectedBrandIds, registerActiveFilterLabels]);

  const { selectedOptions, searchedOptions, hasQuery } = useMemo(() => {
    const query = brandSearch.trim().toLowerCase();
    const selectedBrandIds = new Set(filters.brand_ids.map((id) => Number(id)));

    const selectedOptions = mergedOptions.filter((option) =>
      selectedBrandIds.has(option.erp_id)
    );

    const availableOptions = mergedOptions.filter(
      (option) => !selectedBrandIds.has(option.erp_id)
    );

    const searchedOptions = query
      ? availableOptions.filter((option) =>
          option.name.toLowerCase().includes(query)
        )
      : availableOptions;

    return { selectedOptions, searchedOptions, hasQuery: Boolean(query) };
  }, [brandSearch, filters.brand_ids, mergedOptions]);

  if (isSuccess && !isPending && !mergedOptions.length) return null;

  return (
    <FilterWrapper
      label="brand"
      activeFilterKeys={['brand_ids']}
      {...{
        isFetching,
        isSuccess,
        isPending,
        resultsCount: mergedOptions.length,
        initialOpen: false,
        expandable: true,
        collapsedHeight: isMobile ? 0 : 335,
      }}
    >
      <div className={styles.SearchRow}>
        <div className={styles.SearchInputWrapper}>
          <input
            ref={searchInputRef}
            className={styles.SearchInput}
            type="text"
            placeholder="Search brand"
            value={brandSearch}
            onChange={(event) => setBrandSearch(event.target.value)}
          />
          {brandSearch ? (
            <button
              type="button"
              className={styles.ClearButton}
              aria-label="Clear brand search"
              onClick={() => {
                setBrandSearch('');
                searchInputRef.current?.focus();
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      {selectedOptions.length ? (
        <>
          <div className={styles.SectionLabel}>Selected</div>
          {selectedOptions.map((filterOption) => (
            <FilterOption
              key={`${filterOption.name}-${filterOption.erp_id}`}
              name={filterOption.name}
              value={filterOption.erp_id}
              count={
                filterOption.product_sizes_count ??
                filterOption.attributes_count ??
                0
              }
              filterKey="brand_ids"
            />
          ))}
        </>
      ) : null}

      {hasQuery && !searchedOptions.length && !selectedOptions.length ? (
        <div className={styles.EmptyState}>No brands match your search.</div>
      ) : (
        searchedOptions.map((filterOption) => (
          <FilterOption
            key={`${filterOption.name}-${filterOption.erp_id}`}
            name={filterOption.name}
            value={filterOption.erp_id}
            count={
              filterOption.product_sizes_count ??
              filterOption.attributes_count ??
              0
            }
            filterKey="brand_ids"
          />
        ))
      )}
    </FilterWrapper>
  );
};

export default BrandFilter;
